import { doc, getDoc, collection, query, where, getDocs, getFirestore, writeBatch } from 'firebase/firestore';
import JSZip from 'jszip';
import firebaseApp from '../../firebase/credenciales';

const db = getFirestore(firebaseApp);
const TREG_DEFAULTS = {
  pais_emisor_cod: '604',
  nacionalidad_cod: '604',
  regimen_laboral_cod: '01',
  categoria_trabajador_cod: '01',
  nivel_educativo_cod: '05',
  discapacidad_cod: '0',
  sctr_salud_cod: '0',
  sctr_pension_cod: '0',
  tipo_contrato_cod: '01',
  regimen_jornada_laboral_cod: '1',
  sujeto_fiscalizacion_horario_cod: '1',
  horario_nocturno_cod: '0',
  sindicalizado_cod: '1',
  periodicidad_ingresos_cod: '1',
  percibe_renta5ta_cod: '0',
  ocupacion_publica_cod: '0',
  categoria_periodo_per_cod: '01',
};

/**
 * Genera archivos de carga masiva para T-Registro (.ide, .tri, .per)
 * @param {string} rucEmpresa 
 * @param {Date} fechaCorte 
 * @param {string} planillaId 
 * @param {string[]} empleadoIds 
 * @param {Object} operaciones - Mapa de { empleadoId: 'ALTA' | 'BAJA' | 'MODIFICAR' }
 * @param {Object} motivosBaja - Mapa de { empleadoId: '01'|'02'|'03'... } (Tabla 17 SUNAT)
 */
export const generarTRegistro = async (
  rucEmpresa,
  fechaCorte,
  planillaId = null,
  empleadoIds = null,
  operaciones = {},
  motivosBaja = {}
) => {
  try {
    let ruc = rucEmpresa;
    if (planillaId) {
      const planillaRef = doc(db, "pl_planillas", planillaId);
      const planillaDoc = await getDoc(planillaRef);
      if (planillaDoc.exists()) ruc = planillaDoc.data().ruc_empresa;
    }

    if (!ruc) throw new Error("Se requiere RUC para generar T-Registro");
    await normalizarCamposTRegistroEmpleados(ruc);

    // 1. Obtener empleados
    let empleados = [];
    if (empleadoIds && empleadoIds.length > 0) {
      // Buscar por id de documento Firestore o por DNI para compatibilidad
      const idsObjetivo = new Set(empleadoIds.map((v) => String(v || "").trim()));
      const snap = await getDocs(query(collection(db, "pl_empleados"), where("ruc_empresa", "==", ruc)));
      snap.forEach(docSnap => {
        const data = docSnap.data() || {};
        const docId = String(docSnap.id || "").trim();
        const dni = String(data.dni || "").trim();
        if (idsObjetivo.has(docId) || (dni && idsObjetivo.has(dni))) {
          empleados.push({ id: docId, ...data });
        }
      });
    } else if (planillaId) {
      // Si viene planilla, exportar trabajadores de esa planilla (no solo activos)
      const detalleSnap = await getDocs(
        query(collection(db, "pl_planilla_detalle"), where("planilla_id", "==", planillaId))
      );
      const idsPlanilla = new Set();
      detalleSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const id = String(d.empleado_id || "").trim();
        if (id) idsPlanilla.add(id);
      });

      const empleadosSnap = await getDocs(query(collection(db, "pl_empleados"), where("ruc_empresa", "==", ruc)));
      empleadosSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const docId = String(docSnap.id || "").trim();
        const dni = String(data.dni || "").trim();
        if (idsPlanilla.has(docId) || (dni && idsPlanilla.has(dni))) {
          empleados.push({ id: docId, ...data });
        }
      });
    } else {
      const snap = await getDocs(query(collection(db, "pl_empleados"), where("ruc_empresa", "==", ruc), where("situacion", "==", "ACTIVO")));
      snap.forEach(doc => empleados.push({ id: doc.id, ...doc.data() }));
    }

    if (empleados.length === 0) throw new Error("No hay empleados para exportar");

    // 2. Generar contenido para cada archivo (.ide, .tri, .per)
    const ideContent = empleados.map(emp => generarLineaIDE(emp)).join('\r\n') + '\r\n';
    const triContent = empleados.map(emp => generarLineaTRI(emp, operaciones[emp.id])).join('\r\n') + '\r\n';
    const perContent = empleados
      .map(emp => generarLineaPER(emp, operaciones[emp.id], fechaCorte, motivosBaja[emp.id]))
      .join('\r\n') + '\r\n';

    // 3. Generar ZIP único para SUNAT SOL con nombres RP_<RUC>.<ext>
    const nombreBase = `RP_${ruc}`;
    await descargarZipTRegistro(nombreBase, ideContent, triContent, perContent);

    return { success: true, message: `ZIP T-Registro generado: ${nombreBase}.zip (incluye .ide/.tri/.per)` };
  } catch (error) {
    console.error("Error al generar T-Registro:", error);
    throw error;
  }
};

/**
 * .ide : Datos de Identificación (TipoDoc|NumDoc|PaisEmisor|FechaNac|ApePat|ApeMat|Nombres|Sexo|Nacionalidad|...)
 */
const generarLineaIDE = (emp) => {
  const { apePat, apeMat } = splitApellidos(emp.apellidos);
  const paisEmisor = cleanCode(emp.pais_emisor_cod, TREG_DEFAULTS.pais_emisor_cod);
  const nacionalidad = cleanCode(emp.nacionalidad_cod, TREG_DEFAULTS.nacionalidad_cod);
  return [
    getTipoDocCode(emp.tipo_documento),      // Col 1: Tipo Documento (01=DNI)
    formatPadding(emp.dni || emp.id, 8),     // Col 2: Número Documento (8 dígitos para DNI)
    paisEmisor,                              // Col 3: País Emisor
    formatFechaSlash(emp.fecha_nacimiento || emp.fechaNacimiento),  // Col 4: Fecha Nacimiento (DD/MM/YYYY)
    truncate(apePat, 40),                    // Col 5: Ape Paterno
    truncate(apeMat, 40),                    // Col 6: Ape Materno
    truncate(emp.nombres, 40),               // Col 7: Nombres
    emp.sexo === 'FEMENINO' || emp.sexo === '2' ? '2' : '1', // Col 8: Sexo (1=Masculino, 2=Femenino)
    nacionalidad,                            // Col 9: Nacionalidad
    truncate(emp.telefono || '', 20),        // Col 10: Teléfono (opcional)
    truncate(emp.email || '', 80),           // Col 11: Correo (opcional)
    ''                                       // Pipe final
  ].join('|');
};

/**
 * .tri : Datos Tributarios/Seguridad Social (TipoDoc|NumDoc|RegimenLaboral|Categoria|NivelEduc|Pension|Ingreso|SCTR|...)
 */
const normalizarOperacion = (operacion) => {
  const op = String(operacion || '').toUpperCase();
  if (op === 'ALTA' || op === 'BAJA' || op === 'MODIFICAR') return op;
  return 'MODIFICAR';
};

const generarLineaTRI = (emp, operacion) => {
  const op = normalizarOperacion(operacion);
  const situacion = op === 'BAJA' ? '2' : '1'; // 1=Activo, 2=Baja
  const regimenLaboral = cleanCode(emp.regimen_laboral_cod, TREG_DEFAULTS.regimen_laboral_cod);
  const categoriaTrabajador = cleanCode(emp.categoria_trabajador_cod, TREG_DEFAULTS.categoria_trabajador_cod);
  const nivelEducativo = cleanCode(emp.nivel_educativo_cod, getNivelEducativoCode(emp.nivel_educativo));
  const discapacidad = toFlag(emp.discapacidad_cod, TREG_DEFAULTS.discapacidad_cod);
  const regimenPensionario = cleanCode(
    emp.regimen_pensionario_cod,
    getPensionCode(emp.regimen_pensionario, emp.afp)
  );
  const sctrSalud = cleanEnumCode(emp.sctr_salud_cod, ['0', '1', '2'], TREG_DEFAULTS.sctr_salud_cod);
  const sctrPension = cleanEnumCode(emp.sctr_pension_cod, ['0', '1', '2'], TREG_DEFAULTS.sctr_pension_cod);
  const tipoContrato = cleanCode(emp.tipo_contrato_cod, getTipoContratoCode(emp.tipo_contrato));
  const regimenJornada = toFlag(emp.regimen_jornada_laboral_cod, '1');
  const sujetoFiscalizacion = toFlag(emp.sujeto_fiscalizacion_horario_cod, '1');
  const horarioNocturno = toFlag(emp.horario_nocturno_cod, '0');
  const sindicalizado = toFlag(emp.sindicalizado_cod, TREG_DEFAULTS.sindicalizado_cod);
  const periodicidadIngresos = cleanCode(emp.periodicidad_ingresos_cod, TREG_DEFAULTS.periodicidad_ingresos_cod);
  const percibeRenta5ta = toFlag(emp.percibe_renta5ta_cod, TREG_DEFAULTS.percibe_renta5ta_cod);
  const ocupacionPublica = toFlag(emp.ocupacion_publica_cod, TREG_DEFAULTS.ocupacion_publica_cod);
  
  return [
    getTipoDocCode(emp.tipo_documento),      // Col 1
    formatPadding(emp.dni || emp.id, 8),     // Col 2
    regimenLaboral,                          // Col 3: Régimen Laboral
    categoriaTrabajador,                     // Col 4: Categoría trabajador
    nivelEducativo,                          // Col 5: Nivel Educativo (Tabla 9)
    truncate(emp.ocupacion_cod || '251101', 6), // Col 6: Ocupación (Tabla 10)
    discapacidad,                            // Col 7: Discapacidad (0/1)
    regimenPensionario,                      // Col 8: Régimen Pensionario (Tabla 11)
    formatFechaSlash(emp.fecha_ingreso),      // Col 9: Fecha Ingreso (DD/MM/YYYY)
    sctrSalud,                               // Col 10: SCTR Salud
    sctrPension,                             // Col 11: SCTR Pensión
    tipoContrato,                            // Col 12: Tipo Contrato (Tabla 12)
    situacion,                               // Col 13: Situación (1=Activo, 2=Baja)
    regimenJornada,                          // Col 14: Régimen/Jornada Laboral (0=No, 1=Sí)
    sujetoFiscalizacion,                     // Col 15: Sujeto a fiscalización horaria (0=No, 1=Sí)
    horarioNocturno,                         // Col 16: Horario Nocturno (0=No, 1=Sí)
    sindicalizado,                           // Col 17: Sindicalizado (0=No, 1=Sí)
    periodicidadIngresos,                    // Col 18: Periodicidad Ingresos
    percibeRenta5ta,                         // Col 19: Percibe Rentas 5ta (0=No, 1=Sí)
    ocupacionPublica,                        // Col 20: Ocupación Pública (0/1)
    ''                                       // Pipe final
  ].join('|');
};

/**
 * .per : Datos de Periodos de Alta/Baja (TipoDoc|NumDoc|Categoria|PeriodoInicio|PeriodoFin|MotivoBaja|...)
 */
const generarLineaPER = (emp, operacion, defaultFechaFin, motivoBajaSeleccionado) => {
  const op = normalizarOperacion(operacion);
  const isBaja = op === 'BAJA';
  const fechaFin = emp.fecha_cese || (isBaja ? defaultFechaFin : null);
  const motivoBaja = motivoBajaSeleccionado || emp.motivo_baja_cod || '01';
  const categoriaPeriodo = cleanCode(emp.categoria_periodo_per_cod, TREG_DEFAULTS.categoria_periodo_per_cod);
  
  return [
    getTipoDocCode(emp.tipo_documento),      // Col 1
    formatPadding(emp.dni || emp.id, 8),     // Col 2
    categoriaPeriodo,                        // Col 3: Categoría PER
    formatFechaSlash(emp.fecha_ingreso),     // Col 4: Fecha Inicio
    isBaja ? formatFechaSlash(fechaFin) : '', // Col 5: Fecha Fin
    isBaja ? motivoBaja : '',  // Col 6: Motivo Baja (Tabla 17)
    ''                                       // Pipe final
  ].join('|');
};

// --- Mappings Técnicos SUNAT ---

const getTipoDocCode = (t) => {
  const map = { 'DNI': '01', 'CE': '04', 'PASAPORTE': '07', 'PTP': '04' };
  return map[String(t || 'DNI').toUpperCase()] || '01';
};

const getTipoContratoCode = (t) => {
  const map = { 'INDEFINIDO': '01', 'INDETERMINADO': '01', 'MODALIDAD': '02', 'PARCIAL': '04' };
  return map[String(t || 'INDEFINIDO').toUpperCase()] || '01';
};

const getPensionCode = (reg, afp) => {
  if (reg === 'ONP') return '02'; // DL 19990
  const n = String(afp).toUpperCase();
  if (n.includes('INTEGRA')) return '21';
  if (n.includes('PRIMA')) return '22';
  if (n.includes('PROFUTURO')) return '23';
  if (n.includes('HABITAT')) return '24';
  return '21';
};

const getNivelEducativoCode = (val) => {
  const v = String(val || '').toUpperCase();
  if (v.includes('UNIV') && v.includes('COMP')) return '13';
  if (v.includes('UNIV')) return '12';
  if (v.includes('TECN') && v.includes('COMP')) return '11';
  if (v.includes('SEC') && v.includes('COMP')) return '05';
  if (v.includes('PRIM') && v.includes('COMP')) return '03';
  return '05'; // Default Secundaria Completa
};

const toFlag = (value, fallback = '0') => {
  const normalized = String(value ?? '').trim();
  if (normalized === '0' || normalized === '1') return normalized;
  return fallback;
};

const cleanCode = (value, fallback = '') => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const cleanEnumCode = (value, allowed = [], fallback = '') => {
  const normalized = String(value ?? '').trim();
  if (allowed.includes(normalized)) return normalized;
  return fallback;
};

const toFlagOrNull = (value) => {
  const normalized = String(value ?? '').trim();
  if (normalized === '0' || normalized === '1') return normalized;
  if (value === true) return '1';
  if (value === false) return '0';
  return null;
};

const getPatchCamposTRegistro = (emp) => {
  const patch = {};
  if (!cleanCode(emp.pais_emisor_cod)) patch.pais_emisor_cod = TREG_DEFAULTS.pais_emisor_cod;
  if (!cleanCode(emp.nacionalidad_cod)) patch.nacionalidad_cod = TREG_DEFAULTS.nacionalidad_cod;
  if (!cleanCode(emp.regimen_laboral_cod)) patch.regimen_laboral_cod = TREG_DEFAULTS.regimen_laboral_cod;
  if (!cleanCode(emp.categoria_trabajador_cod)) patch.categoria_trabajador_cod = TREG_DEFAULTS.categoria_trabajador_cod;
  if (!cleanCode(emp.nivel_educativo_cod)) patch.nivel_educativo_cod = TREG_DEFAULTS.nivel_educativo_cod;
  if (!cleanCode(emp.discapacidad_cod)) patch.discapacidad_cod = TREG_DEFAULTS.discapacidad_cod;
  if (!cleanCode(emp.sctr_salud_cod)) patch.sctr_salud_cod = TREG_DEFAULTS.sctr_salud_cod;
  if (!cleanCode(emp.sctr_pension_cod)) patch.sctr_pension_cod = TREG_DEFAULTS.sctr_pension_cod;
  if (!cleanCode(emp.tipo_contrato_cod)) patch.tipo_contrato_cod = TREG_DEFAULTS.tipo_contrato_cod;
  const jornada = toFlagOrNull(emp.regimen_jornada_laboral_cod);
  const fiscalizacion = toFlagOrNull(emp.sujeto_fiscalizacion_horario_cod);
  const nocturno = toFlagOrNull(emp.horario_nocturno_cod);
  const sindicalizado = toFlagOrNull(emp.sindicalizado_cod);
  const percibeRenta5ta = toFlagOrNull(emp.percibe_renta5ta_cod);
  const ocupacionPublica = toFlagOrNull(emp.ocupacion_publica_cod);
  if (!cleanCode(emp.periodicidad_ingresos_cod)) patch.periodicidad_ingresos_cod = TREG_DEFAULTS.periodicidad_ingresos_cod;
  if (!cleanCode(emp.categoria_periodo_per_cod)) patch.categoria_periodo_per_cod = TREG_DEFAULTS.categoria_periodo_per_cod;

  if (!jornada) patch.regimen_jornada_laboral_cod = TREG_DEFAULTS.regimen_jornada_laboral_cod;
  if (!fiscalizacion) patch.sujeto_fiscalizacion_horario_cod = TREG_DEFAULTS.sujeto_fiscalizacion_horario_cod;
  if (!nocturno) patch.horario_nocturno_cod = TREG_DEFAULTS.horario_nocturno_cod;
  if (!sindicalizado) patch.sindicalizado_cod = TREG_DEFAULTS.sindicalizado_cod;
  if (!percibeRenta5ta) patch.percibe_renta5ta_cod = TREG_DEFAULTS.percibe_renta5ta_cod;
  if (!ocupacionPublica) patch.ocupacion_publica_cod = TREG_DEFAULTS.ocupacion_publica_cod;

  return patch;
};

const normalizarCamposTRegistroEmpleados = async (rucEmpresa) => {
  const q = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", rucEmpresa));
  const snap = await getDocs(q);
  if (snap.empty) return;

  let batch = writeBatch(db);
  let opsEnBatch = 0;

  for (const docSnap of snap.docs) {
    const emp = docSnap.data() || {};
    const patch = getPatchCamposTRegistro(emp);
    if (!Object.keys(patch).length) continue;

    batch.update(docSnap.ref, {
      ...patch,
      updated_at: new Date(),
    });
    opsEnBatch += 1;

    if (opsEnBatch >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      opsEnBatch = 0;
    }
  }

  if (opsEnBatch > 0) {
    await batch.commit();
  }
};

// --- Formateo y Descarga ---

const formatPadding = (val, len) => String(val || '').padStart(len, '0');
const truncate = (str, len) => String(str || '').substring(0, len);

const formatFechaSlash = (fecha) => {
  if (!fecha) return '';
  let d;
  if (fecha.seconds) d = new Date(fecha.seconds * 1000);
  else d = new Date(fecha);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const splitApellidos = (apellidos) => {
  if (!apellidos) return { apePat: '', apeMat: '' };
  const parts = apellidos.trim().split(/\s+/);
  return {
    apePat: parts[0] || '',
    apeMat: parts.slice(1).join(' ') || ''
  };
};

const convertirAANSI = (contenido) => {
  // Convertir a Windows-1252 (ANSI) para soporte de "ñ" en validador T-Registro
  const buffer = new Uint8Array(contenido.length);
  for (let i = 0; i < contenido.length; i++) {
    const code = contenido.charCodeAt(i);
    // Mapeo básico de caracteres especiales para Windows-1252
    if (code === 241) buffer[i] = 0xF1; // ñ
    else if (code === 209) buffer[i] = 0xD1; // Ñ
    else if (code === 225) buffer[i] = 0xE1; // á
    else if (code === 233) buffer[i] = 0xE9; // é
    else if (code === 237) buffer[i] = 0xED; // í
    else if (code === 243) buffer[i] = 0xF3; // ó
    else if (code === 250) buffer[i] = 0xFA; // ú
    else if (code === 193) buffer[i] = 0xC1; // Á
    else if (code === 201) buffer[i] = 0xC9; // É
    else if (code === 205) buffer[i] = 0xCD; // Í
    else if (code === 211) buffer[i] = 0xD3; // Ó
    else if (code === 218) buffer[i] = 0xDA; // Ú
    else if (code > 255) buffer[i] = 63; // ?
    else buffer[i] = code;
  }

  return buffer;
};

const descargarZipTRegistro = async (nombreBase, ideContent, triContent, perContent) => {
  const zip = new JSZip();
  zip.file(`${nombreBase}.ide`, convertirAANSI(ideContent));
  zip.file(`${nombreBase}.tri`, convertirAANSI(triContent));
  zip.file(`${nombreBase}.per`, convertirAANSI(perContent));

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = `${nombreBase}.zip`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
